import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatUKDate } from "@/lib/date";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarDays, ChevronDown, ChevronUp, Download, Loader2, Trash2 } from "lucide-react";

const SECTION = "border border-zinc-200 rounded-sm bg-white";
const COLLAPSED_ROWS = 10;

/* ---------------------------------------------------------------------------
 * Match-day scheduling: date-grouped list of every match-day. Editors can
 * change a match's date, kickoff, or delete it entirely (cascades to
 * predictions). Moved to /admin (2026-08) — /editor no longer renders this.
 * -------------------------------------------------------------------------- */
export default function MatchDayList({ refreshKey = 0 }) {
  const [days, setDays] = useState([]);
  const [matchesByDate, setMatchesByDate] = useState({});
  const [openDate, setOpenDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pools");
      setDays(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const toggle = async (date) => {
    if (openDate === date) {
      setOpenDate(null);
      return;
    }
    setOpenDate(date);
    if (!matchesByDate[date]) {
      try {
        const { data } = await api.get(`/matches?date=${date}`);
        setMatchesByDate((m) => ({ ...m, [date]: data || [] }));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
  };

  const move = async (m, newDate) => {
    if (!newDate || newDate === m.match_date) return;
    setBusyId(m.id);
    try {
      await api.patch(`/matches/${m.id}`, { match_date: newDate });
      toast.success(`${m.home_team} vs ${m.away_team} moved to ${newDate}`);
      setMatchesByDate((map) => ({
        ...map,
        [m.match_date]: (map[m.match_date] || []).filter((x) => x.id !== m.id),
      }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const changeKickoff = async (m, newKickoff) => {
    const k = (newKickoff || "").trim();
    if (!k || k === m.kickoff) return;
    setBusyId(m.id);
    try {
      await api.patch(`/matches/${m.id}`, { kickoff: k });
      toast.success(`${m.home_team} vs ${m.away_team} kickoff set to ${k}`);
      setMatchesByDate((map) => ({
        ...map,
        [m.match_date]: (map[m.match_date] || []).map((x) =>
          x.id === m.id ? { ...x, kickoff: k } : x,
        ),
      }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const removeMatch = async (m) => {
    const ok = window.confirm(
      `Delete ${m.home_team} vs ${m.away_team} on ${m.match_date}? This will permanently remove the fixture and any predictions attached to it. Cannot be undone.`,
    );
    if (!ok) return;
    setBusyId(m.id);
    try {
      await api.delete(`/matches/${m.id}?hard=true`);
      toast.success(`${m.home_team} vs ${m.away_team} deleted`);
      setMatchesByDate((map) => ({
        ...map,
        [m.match_date]: (map[m.match_date] || []).filter((x) => x.id !== m.id),
      }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const visibleDays = expanded ? days : days.slice(0, COLLAPSED_ROWS);

  return (
    <section className={`${SECTION} p-6`} data-testid="matchday-list">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-zinc-500" />
          <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950">Match-day scheduling</h2>
        </div>
        {!loading && days.length > 0 && (
          <span className="text-xs uppercase tracking-[0.15em] text-zinc-500" data-testid="matchday-list-count">
            {days.length} match-day{days.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <p className="text-sm text-zinc-500 mb-4 max-w-2xl">
        Click a match-day to expand it. You can change a match&apos;s <strong>date</strong>, edit its <strong>kickoff time</strong>, or
        <strong> delete it entirely</strong>. Predictions follow the match across date moves; deleting also removes every
        prediction submitted on that match. Imported fixtures show a small <em>Imported</em> badge.
      </p>
      {loading ? (
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Loading…</p>
      ) : days.length === 0 ? (
        <p className="text-sm text-zinc-500">No match-days yet — add fixtures using the form above.</p>
      ) : (
        <>
          <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
            {visibleDays.map((d) => {
              const isOpen = openDate === d.match_date;
              const matches = matchesByDate[d.match_date] || [];
              return (
                <div key={d.match_date} data-testid={`matchday-row-${d.match_date}`}>
                  <button
                    onClick={() => toggle(d.match_date)}
                    data-testid={`matchday-toggle-${d.match_date}`}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-50"
                  >
                    <span className="font-semibold text-zinc-950">
                      {formatUKDate(d.match_date, {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                    <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                      {d.match_count} {d.match_count === 1 ? "match" : "matches"}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-4 py-3 bg-zinc-50 space-y-2" data-testid={`matchday-detail-${d.match_date}`}>
                      {matches.length === 0 ? (
                        <p className="text-xs text-zinc-500">No matches on this date.</p>
                      ) : (
                        matches.map((m) => (
                          <div
                            key={m.id}
                            data-testid={`matchday-match-row-${m.id}`}
                            className="flex items-center gap-3 flex-wrap p-3 rounded-sm bg-white border border-zinc-200"
                          >
                            <Input
                              type="time"
                              defaultValue={m.kickoff || ""}
                              onBlur={(e) => changeKickoff(m, e.target.value)}
                              disabled={busyId === m.id}
                              data-testid={`scheduler-kickoff-${m.id}`}
                              title="Kick-off time (24h)"
                              className="h-9 w-28 rounded-sm border-zinc-300 text-sm font-mono tabular-nums"
                            />
                            <span className="text-sm font-semibold text-zinc-950 flex-1 min-w-[160px]">
                              {m.home_team} <span className="text-zinc-400">vs</span> {m.away_team}
                            </span>
                            {m.imported_from === "live-score-api" && (
                              <span
                                data-testid={`scheduler-imported-badge-${m.id}`}
                                title={`Imported from live-score-api${m.competition_label ? ` · ${m.competition_label}` : ""}`}
                                className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px] font-semibold uppercase tracking-wider"
                              >
                                <Download className="h-3 w-3" /> Imported
                              </span>
                            )}
                            <Input
                              type="date"
                              defaultValue={m.match_date}
                              onChange={(e) => move(m, e.target.value)}
                              disabled={busyId === m.id}
                              data-testid={`scheduler-date-${m.id}`}
                              className="h-9 w-44 rounded-sm border-zinc-300 text-sm"
                            />
                            <Button
                              type="button"
                              onClick={() => removeMatch(m)}
                              disabled={busyId === m.id}
                              data-testid={`scheduler-delete-${m.id}`}
                              variant="outline"
                              title="Delete this fixture (removes the match and its predictions permanently)"
                              className="h-9 px-2 rounded-sm border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                            {busyId === m.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {days.length > COLLAPSED_ROWS && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid="matchday-list-toggle"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  Show fewer (first {COLLAPSED_ROWS})
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />
                  Show all {days.length} match-days
                </>
              )}
            </button>
          )}
        </>
      )}
    </section>
  );
}
